import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Button,
    Check,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    TextField,
    TSingleSelectValue,
} from '../../../patterns';
import { StoreContext } from '../base/root-store';
import { getCategoryName } from '../categories/CategoriesUtil';
import { getTooltipButton } from '../../../patterns/components/button/button';

export const CatalogQuestionModal = observer((): JSX.Element => {
    const store = useContext(StoreContext).questionStore;
    const userStore = useContext(StoreContext).userStore;
    const initialCategory = store.variables.catalogQuestionCategory;
    const initialQuestion = store.variables.catalogQuestionInEditing;
    const companyLanguages = userStore.payload!.companyLanguages;

    const [categoryId, setCategoryId] = useState<TSingleSelectValue>(initialCategory ? initialCategory : store.payload!.questionCategories[0].id ?? 'missing-id');
    const [textEnglish, setTextEnglish] = useState<string>(initialQuestion?.questionLanguages.textEnglish ?? '');
    const [textEstonian, setTextEstonian] = useState<string>(initialQuestion?.questionLanguages.textEstonian ?? '');
    const [textFinnish, setTextFinnish] = useState<string>(initialQuestion?.questionLanguages.textFinnish ?? '');
    const [textLatvian, setTextLatvian] = useState<string>(initialQuestion?.questionLanguages.textLatvian ?? '');
    const [textLithuanian, setTextLithuanian] = useState<string>(initialQuestion?.questionLanguages.textLithuanian ?? '');
    const [textPolish, setTextPolish] = useState<string>(initialQuestion?.questionLanguages.textPolish ?? '');
    const [textSwedish, setTextSwedish] = useState<string>(initialQuestion?.questionLanguages.textSwedish ?? '');
    const [textRussian, setTextRussian] = useState<string>(initialQuestion?.questionLanguages.textRussian ?? '');
    const [textJapanese, setTextJapanese] = useState<string>(initialQuestion?.questionLanguages.textJapanese ?? '');
    const [textVietnamese, setTextVietnamese] = useState<string>(initialQuestion?.questionLanguages.textVietnamese ?? '');
    const [isActive, setIsActive] = useState<boolean>(initialQuestion?.isActive ?? false);
    const [activeField, setActiveField] = useState<string | null>(null);

    useEffect(() => {
        setCategoryId(initialCategory ? initialCategory : store.payload!.questionCategories[0].id ?? 'missing-id');
        setTextEnglish(initialQuestion?.questionLanguages.textEnglish ?? '');
        setTextEstonian(initialQuestion?.questionLanguages.textEstonian ?? '');
        setTextFinnish(initialQuestion?.questionLanguages.textFinnish ?? '');
        setTextLatvian(initialQuestion?.questionLanguages.textLatvian ?? '');
        setTextLithuanian(initialQuestion?.questionLanguages.textLithuanian ?? '');
        setTextPolish(initialQuestion?.questionLanguages.textPolish ?? '');
        setTextSwedish(initialQuestion?.questionLanguages.textSwedish ?? '');
        setTextRussian(initialQuestion?.questionLanguages.textRussian ?? '');
        setTextJapanese(initialQuestion?.questionLanguages.textJapanese ?? '');
        setTextVietnamese(initialQuestion?.questionLanguages.textVietnamese ?? '');
        setIsActive(initialQuestion?.isActive ?? false);
        setActiveField(null);
    }, [initialCategory, initialQuestion?.id, store.variables.openModal]);

    const languageMap: Record<string, { state: string; setter: (value: string) => void; labelKey: string }> = {
        en: { state: textEnglish, setter: setTextEnglish, labelKey: 'questions.question-modal.text-english' },
        et: { state: textEstonian, setter: setTextEstonian, labelKey: 'questions.question-modal.text-estonian' },
        fi: { state: textFinnish, setter: setTextFinnish, labelKey: 'questions.question-modal.text-finnish' },
        lv: { state: textLatvian, setter: setTextLatvian, labelKey: 'questions.question-modal.text-latvian' },
        lt: { state: textLithuanian, setter: setTextLithuanian, labelKey: 'questions.question-modal.text-lithuanian' },
        pl: { state: textPolish, setter: setTextPolish, labelKey: 'questions.question-modal.text-polish' },
        sv: { state: textSwedish, setter: setTextSwedish, labelKey: 'questions.question-modal.text-swedish' },
        ru: { state: textRussian, setter: setTextRussian, labelKey: 'questions.question-modal.text-russian' },
        ja: { state: textJapanese, setter: setTextJapanese, labelKey: 'questions.question-modal.text-japanese' },
        vi: { state: textVietnamese, setter: setTextVietnamese, labelKey: 'questions.question-modal.text-vietnamese' },
    };

    return (
        <Modal size="large">
            <ModalHeader>
                <h4>{store.variables.openModal === 'edit-catalog-question' ? store.label('questions.question-modal.title.edit') : store.label('questions.question-modal.title.add')}</h4>
            </ModalHeader>
            <ModalContent>
                <div className="text">
                    <Select
                        label={store.label('questions.question-modal.category')}
                        defaultValue={categoryId}
                        onChange={(value): void => setCategoryId(value)}
                        options={
                            store.payload!.questionCategories.map((category) => category.id).map((oneCategory) => ({
                                label: getCategoryName(userStore.payload!.language, oneCategory ?? 'category-id-not-found', store.categories),
                                value: oneCategory,
                            }))
                        }
                        id='question-modal-category'
                        name='question-modal-category'
                        type={undefined}
                    />
                    {companyLanguages.map((language) => (
                        <div key={language} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <TextField
                                label={store.label(languageMap[language].labelKey)}
                                value={languageMap[language].state}
                                onChange={(value) => {
                                    languageMap[language].setter(value);
                                    setActiveField(language);
                                }}
                                onFocus={() => setActiveField(language)}
                                id={`custom-question-${language}`}
                                name={`custom-question-${language}`}
                                description={store.variables.shouldShowValidation && store.checkText(languageMap[language].state, language)
                                    ? store.label(`common.validation.${store.checkText(languageMap[language].state, language)}`)
                                    : undefined}
                                invalid={store.variables.shouldShowValidation && store.checkText(languageMap[language].state, language) !== undefined}
                            />
                            {activeField === language && getTooltipButton({
                                icon: 'translate',
                                tooltip: store.label('questions.question-modal.translate'),
                                onClick: async () => await store.translateTextForLanguages(language, languageMap[language].state, companyLanguages, languageMap),
                            })}
                        </div>
                    ))}
                    <Check
                        label={store.label('questions.question-modal.is-active')}
                        defaultChecked={isActive}
                        onChange={(checked): void => setIsActive(checked)}
                        id='is-active'
                        name='is-active'
                        value='is-active'/>
                </div>
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block={true} color="white"
                                text={store.label('company.modal.cancel')}/>
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <Button block={true} text={store.label('company.modal.save')}
                            onClick={(): void => store.saveCatalogQuestion({
                                categoryId: categoryId ?? undefined,
                                questionLanguages: {
                                    textEnglish: textEnglish !== '' ? textEnglish : undefined,
                                    textEstonian: textEstonian !== '' ? textEstonian : undefined,
                                    textFinnish: textFinnish !== '' ? textFinnish : undefined,
                                    textLatvian: textLatvian !== '' ? textLatvian : undefined,
                                    textLithuanian: textLithuanian !== '' ? textLithuanian : undefined,
                                    textPolish: textPolish !== '' ? textPolish : undefined,
                                    textSwedish: textSwedish !== '' ? textSwedish : undefined,
                                    textRussian: textRussian !== '' ? textRussian : undefined,
                                    textJapanese: textJapanese !== '' ? textJapanese : undefined,
                                    textVietnamese: textVietnamese !== '' ? textVietnamese : undefined,
                                },
                                isActive,
                            })}/>
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
